import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { tokenStorage } from '../lib/tokenStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const isDev = process.env.NODE_ENV === 'development';

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = tokenStorage.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authIntent, setAuthIntent] = useState(null);

  const fetchMe = useCallback(async () => {
    const t = tokenStorage.get();
    if (!t) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch (e) {
      if (isDev) console.warn('[auth] session invalide, déconnexion.', e?.response?.status);
      tokenStorage.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback((token, u) => {
    tokenStorage.set(token);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    tokenStorage.clear();
    setUser(null);
  }, []);

  const requireAuth = useCallback((intent = null) => {
    if (user) return true;
    setAuthIntent(intent);
    setAuthOpen(true);
    return false;
  }, [user]);

  const value = useMemo(() => ({
    user, loading, login, logout,
    authOpen, setAuthOpen,
    authIntent, setAuthIntent,
    requireAuth,
  }), [user, loading, login, logout, authOpen, authIntent, requireAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
